import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  Typography,
  Button,
  Input,
  Label,
  Textarea,
  NativeSelect,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  toast,
  Alert,
  AlertDescription,
  AlertTitle,
} from "@altimateai/lego";
import { Stack, Spinner, LoadingButton } from "@altimateai/lego";
import { IconUpload, IconFile } from "@tabler/icons-react";
import {
  uploadKnowledgeBaseWithDocument,
  uploadDocumentToKnowledgeBase,
  getKnowledgeBaseWithDocumentStatus,
} from "@/helpers/apis";
import { Privacy } from "@/helpers/constants";
import { BackButton } from "@/components/button";
import { handleFormSubmit } from "./knowledgeBaseUploadUtils";
import { FileUploadSection } from "./components/FileUploadSection";
import { MarkdownContentSection } from "./components/MarkdownContentSection";
import { parseId } from "@/helpers/utils";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { KnowledgeTracking } from "@/lib/tracking/knowledge";

const PRIVACY_OPTIONS = [
  { value: Privacy.PRIVATE, label: "Private" },
  { value: Privacy.PUBLIC, label: "Public" },
];

export default function KnowledgeBaseUploadV2() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: rawId } = useParams();
  const id = parseId(rawId);
  const isEditMode = !!id;

  useBreadcrumb(isEditMode ? "Edit Knowledge" : "Create Knowledge", {
    defaultParents: [
      { label: "Datamates", pathname: "/datamates" },
      { label: "Knowledge Hub", pathname: "/knowledge-hub" },
    ],
  });

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [privacy, setPrivacy] = useState<string>(Privacy.PRIVATE);
  const [icon, setIcon] = useState("📚");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [markdownContent, setMarkdownContent] = useState("");
  const [uploadMode, setUploadMode] = useState<"file" | "markdown">("file");

  const chunkingStrategy = "hybrid";

  const {
    data: existingKB,
    isLoading: isLoadingKB,
    error: loadError,
  } = useQuery({
    queryKey: ["knowledge-base-v2", id],
    queryFn: () => getKnowledgeBaseWithDocumentStatus(id!),
    enabled: isEditMode,
  });

  useEffect(() => {
    if (existingKB && isEditMode) {
      setName(existingKB.name);
      setDescription(existingKB.description || "");
      setPrivacy(existingKB.privacy);
      setIcon(existingKB.icon || "📚");
      // Auto-populate the document content when editing
      if (existingKB.content?.pages?.[0]?.content) {
        setMarkdownContent(existingKB.content.pages[0].content);
        setUploadMode("markdown");
      }
    }
  }, [existingKB, isEditMode]);

  const uploadMutation = useMutation({
    mutationFn: uploadKnowledgeBaseWithDocument,
    onSuccess: data => {
      if (!data) return;
      KnowledgeTracking.sourceCreated(uploadMode, privacy);
      KnowledgeTracking.documentUploaded(
        data.knowledge_base_id,
        uploadMode === "file" ? (selectedFile?.type || "unknown") : "markdown",
      );
      toast({
        title: "Upload started!",
        description: data.message || "Your document is being processed",
      });
      queryClient.invalidateQueries({ queryKey: ["knowledge-base-v2", data.knowledge_base_id] });
      navigate(`/knowledge-hub/${data.knowledge_base_id}`);
    },
    onError: (error: Error) => {
      KnowledgeTracking.sourceCreateFailed(error.message);
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const uploadDocumentMutation = useMutation({
    mutationFn: (formData: FormData) => uploadDocumentToKnowledgeBase(id!, formData),
    onSuccess: data => {
      if (!data) return;
      KnowledgeTracking.sourceEdited(id!);
      KnowledgeTracking.documentUploaded(
        id!,
        uploadMode === "file" ? (selectedFile?.type || "unknown") : "markdown",
      );
      toast({
        title: "Document uploaded!",
        description: data.message || `Version ${data.version} is now processing`,
      });
      queryClient.invalidateQueries({ queryKey: ["knowledge-base-v2", id] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-base-documents", id] });
      navigate(`/knowledge-hub/${id}`);
    },
    onError: (error: Error) => {
      KnowledgeTracking.sourceEditFailed(id!, error.message);
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!name) setName(file.name.replace(/\.[^/.]+$/, ""));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleFormSubmit({
      formData: {
        name,
        description,
        privacy,
        icon,
        uploadMode,
        selectedFile,
        markdownContent,
        chunkingStrategy,
      },
      isEditMode,
      uploadMutation,
      uploadDocumentMutation,
    });
  };

  if (isLoadingKB) {
    return (
      <div className="al-container al-mx-auto al-py-6 al-max-w-4xl">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="al-container al-mx-auto al-py-6 al-max-w-4xl">
      <Stack direction="column" gap="lg">
        <Stack direction="row" justify="between" align="center">
          <Stack direction="column" gap="xs">
            <Stack direction="row" align="center" gap="sm">
              <BackButton defaultUrl="/knowledge-hub" />
              <Typography variant="page-heading">
                {isEditMode ? `Edit: ${existingKB?.name}` : "Create Knowledge Base"}
              </Typography>
            </Stack>
            <Typography variant="body" className="al-text-gray-600">
              {isEditMode
                ? "Upload a new document version to update your knowledge base"
                : "Upload a document to make it queryable via AI in your IDE"}
            </Typography>
          </Stack>
        </Stack>
        {loadError && (
          <Alert variant="destructive">
            <AlertTitle>Failed to load knowledge base</AlertTitle>
            <AlertDescription>
              {loadError instanceof Error ? loadError.message : String(loadError)}
            </AlertDescription>
          </Alert>
        )}
        <Card>
          <CardHeader>
            <CardTitle>
              {isEditMode ? "Upload New Document Version" : "Knowledge Base Details"}
            </CardTitle>
            <CardDescription>
              {isEditMode
                ? "Upload a new document to create a new version. Processing typically takes 5-10 minutes."
                : "Upload a PDF, Word, Markdown, or text file. Images and diagrams are automatically extracted and made searchable."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Stack direction="column" gap="lg">
                {!isEditMode && (
                  <Stack direction="column" gap="md">
                    <div>
                      <Label htmlFor="name">
                        Name <span className="al-text-red-500">*</span>
                      </Label>
                      <Input
                        id="name"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="My Knowledge Base"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        placeholder="What is this knowledge base about?"
                        rows={3}
                      />
                    </div>

                    <div>
                      <Label htmlFor="privacy">Privacy</Label>
                      <NativeSelect
                        id="privacy"
                        value={privacy}
                        onChange={setPrivacy}
                        options={PRIVACY_OPTIONS}
                      />
                    </div>
                  </Stack>
                )}
                <div>
                  <Label>Upload Mode</Label>
                  <Stack direction="row" gap="sm" className="al-mt-2">
                    <Button
                      type="button"
                      variant={uploadMode === "file" ? "default" : "outline"}
                      onClick={() => setUploadMode("file")}
                    >
                      <IconFile className="al-w-4 al-h-4 al-mr-2" />
                      File Upload
                    </Button>
                    <Button
                      type="button"
                      variant={uploadMode === "markdown" ? "default" : "outline"}
                      onClick={() => setUploadMode("markdown")}
                    >
                      Markdown Content
                    </Button>
                  </Stack>
                </div>
                {uploadMode === "file" && (
                  <FileUploadSection
                    selectedFile={selectedFile}
                    handleFileChange={handleFileChange}
                  />
                )}
                {uploadMode === "markdown" && (
                  <MarkdownContentSection
                    markdownContent={markdownContent}
                    setMarkdownContent={setMarkdownContent}
                  />
                )}
                <Alert>
                  <AlertTitle>What happens next?</AlertTitle>
                  <AlertDescription>
                    After upload, your document will be processed in the background. You can close
                    this page and check back later. Once complete, query your knowledge base using
                    the{" "}
                    <code className="al-bg-gray-100 al-px-1 al-rounded">query_knowledge_base</code>{" "}
                    tool in your IDE via DataMates IDE Extension.
                  </AlertDescription>
                </Alert>
                <LoadingButton
                  type="submit"
                  loading={uploadMutation.isPending || uploadDocumentMutation.isPending}
                  className="al-w-full"
                >
                  <IconUpload className="al-w-4 al-h-4 al-mr-2" />
                  {isEditMode ? "Upload New Version" : "Upload & Create Knowledge Base"}
                </LoadingButton>
              </Stack>
            </form>
          </CardContent>
        </Card>
      </Stack>
    </div>
  );
}
